/**
 * MODULE: domain/pages/frontend/utils/pagePipelineSnapshotUtils.ts
 * PURPOSE: Helpers for live CRM pipeline snapshot blocks in note pages
 */

export type PipelineSnapshotBlockContent = {
  title: string;
  show_kpis: boolean;
  show_funnel: boolean;
};

export type PipelineStageCount = {
  stage: string;
  label: string;
  count: number;
};

/** Open pipeline stages shown in the embedded funnel (matches CRM funnel order). */
export const PIPELINE_SNAPSHOT_OPEN_STAGES = [
  'prospecting',
  'discovery',
  'qualified',
  'use_cases',
  'tech_workshop',
  'eb_meeting',
  'bva',
  'eb_playback',
  'pov',
  'paper_process',
  'negotiate_close',
] as const;

export const PIPELINE_SNAPSHOT_STAGE_LABELS: Record<string, string> = {
  prospecting: 'Prospecting',
  discovery: 'Discovery',
  qualified: 'Qualified',
  use_cases: 'Use Cases',
  tech_workshop: 'Tech Workshop',
  eb_meeting: 'EB Meeting',
  bva: 'BVA',
  eb_playback: 'EB Playback',
  pov: 'PoV',
  paper_process: 'Paper Process',
  negotiate_close: 'Negotiate & Close',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

export const DEFAULT_PIPELINE_SNAPSHOT_TITLE = 'Pipeline snapshot';

export function pipelineStageLabel(stage: string): string {
  return PIPELINE_SNAPSHOT_STAGE_LABELS[stage] ?? stage.replace(/_/g, ' ');
}

export function buildPipelineSnapshotContent(input?: Partial<PipelineSnapshotBlockContent>): PipelineSnapshotBlockContent {
  return {
    title: input?.title?.trim() || DEFAULT_PIPELINE_SNAPSHOT_TITLE,
    show_kpis: input?.show_kpis !== false,
    show_funnel: input?.show_funnel !== false,
  };
}

export function readPipelineSnapshotFromAttrs(attrs: Record<string, unknown>): PipelineSnapshotBlockContent {
  return buildPipelineSnapshotContent({
    title: typeof attrs.title === 'string' ? attrs.title : DEFAULT_PIPELINE_SNAPSHOT_TITLE,
    show_kpis: attrs.showKpis !== false,
    show_funnel: attrs.showFunnel !== false,
  });
}

export function pipelineSnapshotContentToAttrs(content: PipelineSnapshotBlockContent): Record<string, unknown> {
  return {
    title: content.title,
    showKpis: content.show_kpis,
    showFunnel: content.show_funnel,
  };
}

export function formatPipelineCurrency(amount: number | null | undefined, currencyCode = 'GBP'): string {
  if (amount == null || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPipelineCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

type DealRow = { id?: string; stage?: string | null };

/** Count open-pipeline deals by funnel stage (excludes draft sentinel and deleted rows). */
export function computePipelineStageCounts(deals: DealRow[]): PipelineStageCount[] {
  const counts = new Map<string, number>();
  for (const stage of PIPELINE_SNAPSHOT_OPEN_STAGES) {
    counts.set(stage, 0);
  }

  for (const deal of deals) {
    const id = deal.id ?? '';
    if (id === '__crm_new__') continue;
    const raw = String(deal.stage ?? 'prospecting').toLowerCase();
    if (raw === 'deleted' || raw === 'closed_won' || raw === 'closed_lost') continue;
    const bucket = (PIPELINE_SNAPSHOT_OPEN_STAGES as readonly string[]).includes(raw) ? raw : 'prospecting';
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }

  return PIPELINE_SNAPSHOT_OPEN_STAGES.map((stage) => ({
    stage,
    label: pipelineStageLabel(stage),
    count: counts.get(stage) ?? 0,
  }));
}

export function pipelineFunnelMaxCount(stageCounts: PipelineStageCount[]): number {
  return Math.max(1, ...stageCounts.map((row) => row.count));
}
